import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import StatusBadge, { URGENCY } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  LayoutGrid, DollarSign, Users, Inbox, CheckCircle2, ShieldCheck, FileText, Clock, Download,
} from "lucide-react";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, tone = "navy", sub }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-500 font-medium">{label}</p>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone === "amber" ? "bg-orange-100 text-sunburst" : tone === "cyan" ? "bg-cyan-100 text-ocean" : "bg-slate-100 text-[#0B192C]"}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className="mt-2 text-3xl font-display font-black text-slate-900 font-mono">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [s, o, v, l] = await Promise.all([
      api.get("/stats"), api.get("/work-orders"), api.get("/vendors"), api.get("/leads"),
    ]);
    setStats(s.data); setOrders(o.data); setVendors(v.data); setLeads(l.data);
  };
  useEffect(() => { load(); }, []);

  const toggleVendor = async (id) => {
    await api.post(`/vendors/${id}/approve`);
    toast.success("Vendor status updated");
    load();
  };

  const filtered = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "dispatch") return o.status === "submitted";
    if (filter === "estimate") return o.status === "awaiting_estimate" && (o.estimate_requests || []).some((r) => r.status === "submitted");
    if (filter === "approval") return o.status === "quote_sent";
    if (filter === "active") return ["awaiting_estimate", "approved", "accepted", "in_progress"].includes(o.status);
    if (filter === "done") return ["completed", "closed"].includes(o.status);
    return true;
  });

  return (
    <DashboardShell>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-black text-slate-900">Command Center</h1>
          <p className="text-slate-500 text-sm mt-1">Dispatch, price, and settle work orders across Florida.</p>
        </div>
      </div>

      {stats && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Inbox} label="Pending Dispatch" value={stats.pending_dispatch} tone="amber" sub={`${stats.total_orders} total orders`} />
          <Stat icon={Clock} label="Awaiting Approval" value={stats.awaiting_approval} tone="cyan" />
          <Stat icon={LayoutGrid} label="Active Jobs" value={stats.in_progress} />
          <Stat icon={DollarSign} label="Markup Revenue" value={`$${stats.markup_revenue}`} tone="amber" sub={`$${stats.gross_billed} gross billed`} />
        </div>
      )}

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="orders" data-testid="tab-orders">Work Orders</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors {stats?.pending_vendors ? <Badge className="ml-2 bg-sunburst">{stats.pending_vendors}</Badge> : null}</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads {leads.length ? <span className="ml-1 text-xs text-slate-400">({leads.length})</span> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <div className="flex gap-2 flex-wrap mb-4">
            {[["all", "All"], ["dispatch", "Pending Dispatch"], ["estimate", "Estimates In"], ["approval", "Awaiting Approval"], ["active", "Active"], ["done", "Completed"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === k ? "bg-[#0B192C] text-white" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Ref</TableHead><TableHead>Property</TableHead><TableHead>Service</TableHead>
                  <TableHead>Urgency</TableHead><TableHead>Vendor</TableHead><TableHead>PM Price</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-wo-row-${o.ref}`} className="cursor-pointer hover:bg-orange-50/40" onClick={() => navigate(`/orders/${o.id}`)}>
                    <TableCell className="font-mono text-xs font-semibold text-ocean">{o.ref}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-sm">{o.property_address}</TableCell>
                    <TableCell className="text-sm">{o.service_type}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY[o.urgency]?.cls}`}>{URGENCY[o.urgency]?.label}</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{o.vendor_name || "—"}</TableCell>
                    <TableCell className="font-mono text-sm font-semibold">{o.pm_price != null ? `$${o.pm_price}` : "—"}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No work orders here.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="vendors" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {vendors.map((v) => (
              <div key={v.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`vendor-card-${v.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold text-slate-900">{v.name}</p>
                    <p className="text-sm text-slate-400">{v.company_name} · {v.email}</p>
                  </div>
                  {v.vendor_approved
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><ShieldCheck className="w-3 h-3 mr-1" />Approved</Badge>
                    : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {v.service_categories.map((c) => <span key={c} className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{c}</span>)}
                </div>
                <div className="mt-3 space-y-1.5">
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Compliance docs</p>
                  {v.docs.length === 0 && <p className="text-sm text-slate-400">No documents uploaded</p>}
                  {v.docs.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <span className="capitalize text-slate-600">{d.doc_type}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-xs text-slate-400 truncate flex-1">{d.original_filename}</span>
                      <button onClick={() => downloadFile(d.storage_path, d.original_filename)} title="Download" data-testid={`download-doc-${d.id}`} className="text-slate-400 hover:text-sunburst">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {d.status === "verified"
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Verified</Badge>
                        : <button onClick={() => api.post(`/vendors/docs/${d.id}/verify`).then(load)} className="text-xs text-ocean font-semibold" data-testid={`verify-doc-${d.id}`}>Verify</button>}
                    </div>
                  ))}
                </div>
                <Button data-testid={`vendor-approve-${v.id}`} onClick={() => toggleVendor(v.id)} variant={v.vendor_approved ? "outline" : "default"}
                  className={`mt-4 w-full ${v.vendor_approved ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-[#0B192C] hover:bg-[#1E3E62] text-white"}`}>
                  {v.vendor_approved ? "Revoke approval" : "Approve vendor"}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leads" className="mt-4">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Contact</TableHead><TableHead>Message</TableHead><TableHead>When</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{l.lead_type.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{l.email}<br />{l.phone}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-sm text-slate-500">{l.message}</TableCell>
                    <TableCell className="text-xs text-slate-400 font-mono">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No leads yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
